import {
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Typography,
} from "@material-ui/core";
import { Check, Close } from "@material-ui/icons";
import {useEffect, useMemo, useState} from "react";
import { Spacer } from "../../spacer";

const ALTERNATIVE_LETTERS = ['A', 'B', 'C', 'D', 'E'];

const ReviewStepIcon = ({ correct }) => (
    <div style={{ color: correct ? 'green' : 'red' }}>
        {correct ? <Check /> : <Close />}
    </div>
);

const AssessmentQuestionWizard = ({
    questions,
    onFinish,
    forceFinish = false,
    onForceFinish,
    disabled = false,
    readOnly = false,
    answers: answersProp = {},
    response,
}) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [internalAnswers, setInternalAnswers] = useState({});
    const [answered, setAnswered] = useState({});

    const answers = useMemo(() => {
        if (readOnly) {
            return response ? response.reduce((acc, r) => {
                acc[r.questionId] = r.optionId
                return acc
            }, {}) : {}
        }
        return internalAnswers;
    }, [internalAnswers, readOnly, response]);

    const correctAnswers = useMemo(() => {
        if (!readOnly) return {};
        return response ? response.reduce((acc, r) => {
            acc[r.questionId] = r.isCorrect
            return acc
        }, {}) : {};
    }, [response, readOnly])

    const currentQuestion = questions[currentQuestionIndex];
    const allAnswered = Object.keys(answered).length >= questions.length;

    useEffect(() => {
        if (forceFinish) {
            (onForceFinish ?? onFinish)?.({ answers: internalAnswers, answered });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceFinish]);

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1)
            setCurrentQuestionIndex((prev) => prev + 1);
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0)
            setCurrentQuestionIndex((prev) => prev - 1);
    };

    const handleAnswerChange = (optionId) => {
        setInternalAnswers((prev) => ({ ...prev, [currentQuestion.id]: optionId }));
        setAnswered((prev) => ({ ...prev, [currentQuestion.id]: true }));
    };

    const isQuestionCorrect = (question) => {
        return correctAnswers[question.id] === true;
    };

    const finishLabel = "Finalizar";
    const getStepLabel = (q, i) => `Questão ${i + 1}`;

    const getOptionStyle = (option) => {
        if (!readOnly) return {};
        if (option.isCorrect) return { color: 'green' };
        if (option.id === answers[currentQuestion?.id]) return { color: 'red' };
        return {};
    };

    return (
        <>
            <div
                style={{
                    width: "77vw",
                    overflowX: "auto",
                    maxWidth: "100%",
                    backgroundColor: "#373737",
                    borderRadius: "8px",
                }}
            >
                <Stepper
                    orientation="horizontal"
                    nonLinear
                    dividers
                    activeStep={currentQuestionIndex}
                    alternativeLabel
                >
                    {questions.map((q, index) => (
                        <Step key={q.id ?? index}>
                            <StepButton
                                onClick={() => setCurrentQuestionIndex(index)}
                                completed={readOnly ? !!answers[q.id] : !!answered[q.id]}
                            >
                                <StepLabel
                                    StepIconComponent={readOnly ? ReviewStepIcon : undefined}
                                    StepIconProps={readOnly ? { correct: isQuestionCorrect(q) } : undefined}
                                >
                                    {getStepLabel(q, index)}
                                </StepLabel>
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            </div>

            {currentQuestion && (
                <Box marginTop="2rem">
                    <Divider style={{ marginBlock: "8px" }} />
                    <Typography
                        variant="h6"
                        color="textSecondary"
                        align="justify"
                        gutterBottom
                        style={{ whiteSpace: "pre-line" }}
                    >
                        {currentQuestion.code} - {currentQuestion.question}
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            style={{ marginTop: "2rem" }}
                            value={answers[currentQuestion.id] || ""}
                            onChange={(e) => !readOnly && handleAnswerChange(e.target.value)}
                        >
                            {currentQuestion.options?.map((option, idx) => (
                                <FormControlLabel
                                    key={option.id}
                                    value={option.id}
                                    control={<Radio style={{ alignSelf: "start", padding: "3px 9px 9px 9px" }} />}
                                    label={`${ALTERNATIVE_LETTERS[idx]}) ${option.optionText}`}
                                    disabled={readOnly || disabled}
                                    style={{ marginBlock: "0.5rem", alignSelf: "start", ...getOptionStyle(option) }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>
            )}

            <Box display="flex" marginTop="2rem">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    style={{ marginRight: "8px" }}
                >
                    Anterior
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disabled={currentQuestionIndex === questions.length - 1}
                >
                    Próxima
                </Button>
                {!readOnly && (
                    <>
                        <Spacer />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => onFinish({ answers: internalAnswers, answered })}
                            disabled={disabled || !allAnswered}
                        >
                            {finishLabel}
                        </Button>
                    </>
                )}
            </Box>
        </>
    );
};

export default AssessmentQuestionWizard;
